import { useCallback, useEffect, useRef, useState } from "react";

import { ApiError, type CurrentPerson, type SessionApi, type SessionState } from "../api/client";
import { sessionStatus, type SessionStatus } from "./status";

/**
 * Die Sitzung, wie die Oberfläche sie sieht — eine Abfrage mit drei Anlässen,
 * alle drei dieselbe Frage an denselben Endpunkt:
 *
 * | wann | wozu |
 * | --- | --- |
 * | beim Start | einmal fragen: angemeldet oder Anmeldemaske |
 * | alle 60 Sekunden | der Keepalive — das ERP-Timeout sind 30 Minuten |
 * | beim Zurückkommen ins Fenster | nach der Mittagspause ist die Sitzung weg, und das soll man erfahren, bevor man etwas eintippt |
 *
 * **Der Takt hält an, wenn das Fenster im Hintergrund ist.** Das ist richtig so:
 * Ein Ping aus einem Hintergrundtab hielte die Sitzung eines Menschen offen, der
 * nicht da ist.
 */
const KEEPALIVE_MILLISECONDS = 60_000;

export interface SessionView {
  status: SessionStatus;
  state: SessionState | null;
  person: CurrentPerson | null;
  /** Die Meldung zum Zustand `unavailable` — sonst leer. */
  message: string;
  signedIn: (state: SessionState) => void;
  signOut: () => void;
}

export function useSession(client: SessionApi): SessionView {
  const [state, setState] = useState<SessionState | null>(null);
  const [person, setPerson] = useState<CurrentPerson | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const wasSignedIn = useRef(false);

  const look = useCallback((): void => {
    client.session().then(
      (answer) => {
        wasSignedIn.current = true;
        setState(answer);
        setError(null);
      },
      (reason: unknown) => {
        setState(null);
        setPerson(null);
        setError(reason instanceof ApiError ? reason : new ApiError(String(reason)));
      },
    );
  }, [client]);

  // Die eigene Person wird nur geholt, wenn es eine Sitzung gibt — und dann
  // einmal. Sie steckt nicht in der Sitzung: Eine Rolle in der Sitzung wäre eine
  // Einladung an die Oberfläche, aus ihr zu folgern, was jemand darf.
  useEffect(() => {
    if (state === null || person !== null) {
      return;
    }

    let discarded = false;

    client.currentPerson().then(
      (answer) => {
        if (!discarded) {
          setPerson(answer);
        }
      },
      () => {
        /* Ohne eigene Person bleibt die Marke leer — erfunden wird keine. */
      },
    );

    return () => {
      discarded = true;
    };
  }, [client, state, person]);

  useEffect(() => {
    look();

    const tick = window.setInterval(() => {
      if (document.visibilityState === "visible") {
        look();
      }
    }, KEEPALIVE_MILLISECONDS);

    const onVisible = (): void => {
      if (document.visibilityState === "visible") {
        look();
      }
    };

    document.addEventListener("visibilitychange", onVisible);

    return () => {
      window.clearInterval(tick);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [look]);

  const status = sessionStatus({ state, error, wasSignedIn: wasSignedIn.current });

  return {
    status,
    state,
    person,
    message: status === "unavailable" && error !== null ? error.message : "",

    signedIn: (answer: SessionState): void => {
      wasSignedIn.current = true;
      setState(answer);
      setError(null);
    },

    signOut: (): void => {
      // Wer abmeldet, ist danach abgemeldet — auch wenn die Gegenstelle dabei
      // stolpert. Der lokale Zustand fällt in jedem Fall.
      const done = (): void => {
        wasSignedIn.current = false;
        setState(null);
        setPerson(null);
        setError(new ApiError("Sie sind nicht angemeldet.", "not_signed_in"));
      };

      client.signOut().then(done, done);
    },
  };
}
